import React, { useState, useEffect } from 'react';
import { Button } from '@zurich-primitives/button';
import { Modal } from '@zurich-base-ui/components.modal';
import { LanguageSelector, Language } from 'app/components/LanguageSelector';
import { getAvailableFileTranslatorLanguages } from 'integration';
import styles from './TranslateDocument.module.scss';
import { ITranslateDocumentModal } from './TranslateDocument.interface';

export const TranslateDocumentModal: React.FC<ITranslateDocumentModal> = ({
  isOpen,
  onClose,
  onTranslate,
  documentName
}) => {
  const [availableLanguages, setAvailableLanguages] = useState<Language[]>([]);
  const [selectedLanguage, setSelectedLanguage] = useState<Language | null>(
    null
  );
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setIsLoading(true);
      getAvailableFileTranslatorLanguages('cross-functional', 'translator')
        .then((response) => {
          const mappedLanguages = response.map((language: any) => ({
            name: language.name,
            labelName: language.name,
            code: language.code
          }));
          setAvailableLanguages(mappedLanguages);
          if (mappedLanguages.length > 0) {
            setSelectedLanguage(mappedLanguages[0]);
          }
        })
        .catch((error) => {
          console.error('Error getting available languages:', error);
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [isOpen]);

  const handleTranslate = (): void => {
    if (selectedLanguage) {
      onTranslate(selectedLanguage);
      onClose();
    }
  };

  const handleLanguageChange = (e: { target: { value: any } }): void => {
    setSelectedLanguage(e.target.value);
  };

  const handleClose = (e: { stopPropagation: () => void }): void => {
    e.stopPropagation();
    onClose();
  };

  return (
    <Modal on={isOpen} onClose={handleClose} variant="slim">
      <div className={`${styles.modal} p-8 pt-6`}>
        <h2 className="f-data-blue highlight">Translate Document</h2>

        <p className={styles['document-name']}>{documentName}</p>

        <span className={styles.wrapper}>
          <p>Selected language:</p>
          <div className={styles['language-selector-container']}>
            <LanguageSelector
              languages={availableLanguages}
              selectedLanguage={selectedLanguage?.name ?? ''}
              handleOnLanguageSelectorChange={handleLanguageChange}
              isLoading={isLoading}
            />
          </div>
        </span>

        <p className={styles.disclaimer}>
          Using the translation function is subject to a fee (run through the AI
          Lab&apos;s cost center). The fee is approximately 5 cents per page.
          For this reason, we ask you to refrain from translating very long
          documents (more than 500 pages) and generally use this resource
          sparingly. Thank you!
        </p>

        <div className={styles.buttons}>
          <Button variant="secondary" onClick={onClose}>
            <Button.Label>Cancel</Button.Label>
          </Button>

          <Button
            onClick={handleTranslate}
            disabled={!selectedLanguage || isLoading}
          >
            <Button.Label>Translate</Button.Label>
          </Button>
        </div>
      </div>
    </Modal>
  );
};
